import { OneEuroFilter } from '../smoothing/OneEuroFilter';
import { LM, lmX, lmY, pixelDistance, type LandmarkBuffer } from '../tracking/landmarks';

/**
 * Horizontal visible iris diameter. Remarkably constant across adults
 * (11.7 ± 0.5 mm, 1σ), which is why it is the standard metric anchor for
 * markerless face measurement (MediaPipe Iris, Google 2020).
 *
 * Consequence to keep in mind: the population spread (±4 %) is a *systematic*
 * per-person error on the absolute scale — ±6 mm on a 150 mm frame at 1σ.
 * The 2 mm acceptance target is met on average, not for every individual;
 * a credit-card calibration step (Phase 3) is the way to reach ±1 mm.
 */
export const IRIS_DIAMETER_MM = 11.7;

export interface IrisMeasurement {
  /** Right eye iris diameter in pixels (max of horizontal / vertical extent). */
  rightPx: number;
  leftPx: number;
  /** Combined estimate used for depth. */
  meanPx: number;
  /** Eye aspect ratio (min of both eyes): < 0.18 means eyes (almost) closed. */
  eyeOpenness: number;
}

/**
 * Measures the iris diameter in pixels from the 5-point iris landmarks.
 * Under yaw the horizontal extent shrinks (cos), under pitch the vertical one;
 * taking the larger of the two per eye gives an estimate that is robust to
 * moderate head rotation without needing the pose.
 */
export function measureIris(buf: LandmarkBuffer, width: number, height: number): IrisMeasurement {
  const r = LM.RIGHT_IRIS;
  const l = LM.LEFT_IRIS;
  const rightPx = Math.max(pixelDistance(buf, r[0], r[2], width, height), pixelDistance(buf, r[1], r[3], width, height));
  const leftPx = Math.max(pixelDistance(buf, l[0], l[2], width, height), pixelDistance(buf, l[1], l[3], width, height));

  // If one iris is much smaller (foreshortened / partially occluded), trust the other.
  const ratio = Math.min(rightPx, leftPx) / Math.max(rightPx, leftPx, 1e-6);
  const meanPx = ratio < 0.8 ? Math.max(rightPx, leftPx) : (rightPx + leftPx) / 2;

  const earRight =
    pixelDistance(buf, LM.RIGHT_EYE_TOP, LM.RIGHT_EYE_BOTTOM, width, height) /
    Math.max(1e-6, pixelDistance(buf, LM.RIGHT_EYE_OUTER, LM.RIGHT_EYE_INNER, width, height));
  const earLeft =
    pixelDistance(buf, LM.LEFT_EYE_TOP, LM.LEFT_EYE_BOTTOM, width, height) /
    Math.max(1e-6, pixelDistance(buf, LM.LEFT_EYE_OUTER, LM.LEFT_EYE_INNER, width, height));

  return { rightPx, leftPx, meanPx, eyeOpenness: Math.min(earRight, earLeft) };
}

/** Pinhole: depth = f * realSize / pixelSize. */
export function depthFromIrisPx(irisPx: number, focalPx: number): number {
  return (focalPx * IRIS_DIAMETER_MM) / Math.max(irisPx, 1e-3);
}

/** Pixel midpoint between the two iris centres. */
export function irisMidpointPx(buf: LandmarkBuffer, width: number, height: number): { u: number; v: number } {
  return {
    u: ((lmX(buf, LM.RIGHT_IRIS_CENTER) + lmX(buf, LM.LEFT_IRIS_CENTER)) / 2) * width,
    v: ((lmY(buf, LM.RIGHT_IRIS_CENTER) + lmY(buf, LM.LEFT_IRIS_CENTER)) / 2) * height,
  };
}

export interface DepthSample {
  /** Optional absolute reference from a user-supplied PD; null means unavailable. */
  metricDepthMm?: number | null;
  /** Iris diameter in pixels. */
  irisPx: number;
  /** Camera focal length in pixels. */
  focalPx: number;
  /** Whether the iris measurement is trustworthy (frontal enough, eyes open, big enough). */
  reliable: boolean;
  /** Depth of the face origin reported by MediaPipe's matrix (its own units), or null. */
  mediapipeDepth: number | null;
  tSeconds: number;
}

export interface DepthEstimatorOptions {
  /** Number of reliable samples aggregated by median before the scale filter takes over. */
  warmupSamples: number;
  /** Cutoff (Hz) of the filter on the iris/MediaPipe scale ratio — a per-person constant, so very low. */
  minCutoff: number;
  /** Adaptive term of the scale filter (0 = fixed cutoff). */
  beta: number;
  /** Maximum rate of change of the output depth (mm/s). People lean in at 100–250 mm/s; noise is much faster. */
  maxSlewMmPerS: number;
  /** Optional pre-filter cutoff. Zero leaves dynamics to the final pose smoother (avoids double filtering). */
  mpCutoff: number;
  /** Faster safety bound for the wide, rotation-corrected pupil baseline. */
  metricMaxSlewMmPerS: number;
}

/**
 * Hybrid metric depth estimator.
 * With supplied/estimated PD, the reliable wide pupil baseline drives depth
 * directly through an adaptive filter; MP carries motion only while gated.
 * The slower iris-only path below remains for callers without PD.
 *
 *  - The *dynamics* (leaning in, stepping back, per frame) come from
 *    MediaPipe's own pose depth: a least-squares fit over 468 landmarks, smooth
 *    and immediate, but with no absolute scale (it assumes an average-sized
 *    face).
 *  - The *absolute scale* comes from the iris diameter (pinhole model). The
 *    ratio iris-depth / MediaPipe-depth is a per-person constant, so it is
 *    filtered very slowly (~1 s): iris jitter, which is large at a distance,
 *    never reaches the frame size, while a real approach is followed at once.
 *  - When the iris is unreliable (large yaw, blink) the scale simply stays.
 *  - Without a transformation matrix the iris depth is filtered directly.
 *
 * Warm-up: the first `warmupSamples` reliable ratios are combined with a
 * median so the scale is accurate within ~0.3 s instead of creeping in.
 */
export class DepthEstimator {
  private readonly opts: DepthEstimatorOptions;
  private readonly scaleFilter: OneEuroFilter;
  private readonly irisFilter: OneEuroFilter;
  private readonly mpFilter: OneEuroFilter;
  private readonly metricFilter = new OneEuroFilter({ minCutoff: 1.2, beta: 0.06, dCutoff: 4 });
  private readonly metricWindow: number[] = [];
  private metricLastTime = -1;
  private readonly warmup: number[] = [];
  private scale: number | null = null;
  private filteredIrisDepth: number | null = null;
  private lastOutput: number | null = null;
  private lastOutputTime = -1;

  constructor(opts: Partial<DepthEstimatorOptions> = {}) {
    this.opts = { warmupSamples: 8, minCutoff: 0.15, beta: 0, maxSlewMmPerS: 250, mpCutoff: 0, metricMaxSlewMmPerS: 1500, ...opts };
    this.scaleFilter = new OneEuroFilter({ minCutoff: this.opts.minCutoff, beta: this.opts.beta, dCutoff: 1 });
    this.irisFilter = new OneEuroFilter({ minCutoff: 0.6, beta: 0.03, dCutoff: 2 });
    this.mpFilter = new OneEuroFilter({ minCutoff: this.opts.mpCutoff, beta: 0.02, dCutoff: 2 });
  }

  /** Returns the estimated depth (mm) of the iris/cornea plane, or null before any reliable sample. */
  update(s: DepthSample): number | null {
    const rawMp = s.mediapipeDepth !== null && s.mediapipeDepth > 1e-6 ? s.mediapipeDepth : null;
    const mp = rawMp !== null && this.opts.mpCutoff > 0 ? this.mpFilter.filter(rawMp, s.tSeconds) : rawMp;

    let target: number | null;
    const reference = s.metricDepthMm === undefined ? depthFromIrisPx(s.irisPx, s.focalPx) : s.metricDepthMm;
    if (s.reliable && s.irisPx > 3 && reference !== null && Number.isFinite(reference) && reference > 0) {
      const rawIris = reference;
      if (s.metricDepthMm !== undefined) {
        // PD spans both eyes: use its observable distance directly, rather than
        // waiting seconds for iris/MP calibration to catch a stale matrix Z.
        // A three-sample median rejects isolated landmark spikes. Adaptive
        // smoothing suppresses rest jitter while following deliberate movement.
        if (s.tSeconds - this.metricLastTime > 0.2) {
          this.metricWindow.length = 0;
          this.metricFilter.reset();
          if (this.lastOutput !== null) this.metricFilter.filter(this.lastOutput, s.tSeconds - 1 / 30);
        }
        this.metricLastTime = s.tSeconds;
        this.metricWindow.push(rawIris);
        if (this.metricWindow.length > 3) this.metricWindow.shift();
        target = this.metricFilter.filter(median(this.metricWindow), s.tSeconds);
        if (this.warmup.length < this.opts.warmupSamples) this.warmup.push(rawIris);
      } else if (mp !== null) {
        // Calibrate from synchronous measurements: a delayed denominator mistakes
        // a real approach for a change in the wearer's physical scale.
        const ratio = rawIris / rawMp!;
        if (this.warmup.length < this.opts.warmupSamples) {
          this.warmup.push(ratio);
          const med = median(this.warmup);
          this.scaleFilter.reset();
          this.scale = this.scaleFilter.filter(med, s.tSeconds);
        } else {
          this.scale = this.scaleFilter.filter(ratio, s.tSeconds);
        }
        target = mp * this.scale;
      } else {
        if (this.warmup.length < this.opts.warmupSamples) {
          this.warmup.push(rawIris);
          this.irisFilter.reset();
          target = this.irisFilter.filter(median(this.warmup), s.tSeconds);
        } else {
          target = this.irisFilter.filter(rawIris, s.tSeconds);
        }
      }
      this.filteredIrisDepth = target;
    } else if (this.filteredIrisDepth === null) {
      return null;
    } else if (mp !== null && this.scale !== null) {
      // Iris gated (yaw, blink): MediaPipe carries the motion at the calibrated scale.
      target = mp * this.scale;
    } else {
      target = this.filteredIrisDepth;
    }

    // Keep a conservative bound for noisy iris-only depth; the wide PD
    // baseline can follow faster deliberate near/far movement.
    if (this.lastOutput !== null && this.lastOutputTime >= 0 && !this.isWarmingUp()) {
      const dt = Math.max(1e-3, s.tSeconds - this.lastOutputTime);
      const maxStep = (s.metricDepthMm !== undefined ? this.opts.metricMaxSlewMmPerS : this.opts.maxSlewMmPerS) * dt;
      target = clamp(target, this.lastOutput - maxStep, this.lastOutput + maxStep);
    }
    // Anchor gated tracking to the last actually displayed metric depth.
    // Otherwise turning/blinking after an approach would restore old scale.
    if (s.metricDepthMm !== undefined && s.reliable && mp !== null) this.scale = target / mp;
    this.lastOutput = target;
    this.lastOutputTime = s.tSeconds;
    return target;
  }

  private isWarmingUp(): boolean {
    return this.warmup.length < this.opts.warmupSamples;
  }

  get calibrated(): boolean {
    return this.warmup.length >= this.opts.warmupSamples;
  }

  get value(): number | null {
    return this.lastOutput;
  }

  /** Iris / MediaPipe depth ratio (null until calibrated or without matrix). Debug. */
  get scaleRatio(): number | null {
    return this.scale;
  }

  reset(): void {
    this.warmup.length = 0;
    this.scaleFilter.reset();
    this.irisFilter.reset();
    this.mpFilter.reset();
    this.metricFilter.reset();
    this.metricWindow.length = 0;
    this.metricLastTime = -1;
    this.scale = null;
    this.filteredIrisDepth = null;
    this.lastOutput = null;
    this.lastOutputTime = -1;
  }
}

export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
